import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-timezone';
import classNames from 'classnames';
import {
  XMarkIcon,
  LockClosedIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardDocumentListIcon,
  CheckIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import {
  availabilityMessage,
  getOrderAccessState,
  parseApiError,
  provisionOrderAccess,
  revealOrderAccessPin,
  revokeAccess,
} from './smartLockApi';
import useSmartLockSummary, { invalidateSmartLockSummary } from './useSmartLockSummary';

const INPUT_FMT = 'YYYY-MM-DDTHH:mm';
const PIN_VISIBLE_MS = 60 * 1000;

const STATUS_STYLES = {
  scheduled: 'bg-slate-100 text-slate-600',
  provisioning: 'bg-sky-50 text-sky-700',
  provisioned: 'bg-sky-50 text-sky-700',
  ready: 'bg-emerald-50 text-emerald-700',
  active: 'bg-emerald-100 text-emerald-800',
  revoking: 'bg-amber-50 text-amber-700',
  revocation_unconfirmed: 'bg-amber-100 text-amber-800',
};
const STATUS_LABEL = {
  scheduled: 'Scheduled',
  provisioning: 'Creating…',
  provisioned: 'Syncing to lock',
  ready: 'Code ready',
  active: 'Code in use',
  revoking: 'Revoking…',
  revocation_unconfirmed: 'Revoke unconfirmed',
};

const toInput = (iso, tz) => (iso && tz ? moment.tz(iso, tz).format(INPUT_FMT) : '');
const fromInput = (value, tz) => (value && tz ? moment.tz(value, INPUT_FMT, tz).toISOString() : null);
const fmtWindow = (iso, tz) => (iso && tz ? moment.tz(iso, tz).format('ddd MMM D, h:mm A') : '—');
const tzAbbr = (tz) => (tz ? moment.tz(tz).zoneAbbr() : '');

function StatusPill({ status }) {
  if (!status) return null;
  return (
    <span className={classNames('inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold', STATUS_STYLES[status] || 'bg-slate-100 text-slate-600')}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function SmartLockAccessModal({ orderId, orderLabel, onClose }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // 'provision' | 'reveal' | 'revoke'
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');
  const [pin, setPin] = useState(null);
  const [copied, setCopied] = useState(false);
  const pinTimer = useRef(null);
  const touchedRef = useRef(false);

  const tz = state?.timeZone || null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getOrderAccessState(orderId);
      setState(data);
      if (!touchedRef.current) {
        const s = data.access?.authorizedStartAt && ['scheduled', 'provisioning', 'provisioned', 'ready', 'active'].includes(data.access.status)
          ? data.access.authorizedStartAt
          : data.suggestedWindow?.startAt;
        const e = data.access?.authorizedEndAt && ['scheduled', 'provisioning', 'provisioned', 'ready', 'active'].includes(data.access.status)
          ? data.access.authorizedEndAt
          : data.suggestedWindow?.endAt;
        setStartInput(toInput(s, data.timeZone));
        setEndInput(toInput(e, data.timeZone));
      }
    } catch (err) {
      setError(parseApiError(err, 'Unable to load smart lock status'));
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
    return () => {
      if (pinTimer.current) clearTimeout(pinTimer.current);
    };
  }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const hidePin = useCallback(() => {
    setPin(null);
    setCopied(false);
    if (pinTimer.current) clearTimeout(pinTimer.current);
  }, []);

  const handleProvision = async () => {
    setBusy('provision');
    setError(null);
    try {
      const startsAt = fromInput(startInput, tz);
      const endsAt = fromInput(endInput, tz);
      const data = await provisionOrderAccess(orderId, { startsAt, endsAt });
      setState(data);
      invalidateSmartLockSummary(orderId);
      hidePin();
    } catch (err) {
      setError(parseApiError(err, 'Could not create access'));
    } finally {
      setBusy(null);
    }
  };

  const handleReveal = async () => {
    setBusy('reveal');
    setError(null);
    try {
      const data = await revealOrderAccessPin(orderId);
      setPin(data.pin);
      if (pinTimer.current) clearTimeout(pinTimer.current);
      pinTimer.current = setTimeout(hidePin, PIN_VISIBLE_MS);
    } catch (err) {
      setError(parseApiError(err, 'Could not reveal PIN'));
    } finally {
      setBusy(null);
    }
  };

  const handleRevoke = async () => {
    if (!state?.access?.id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Revoke this access code now? The concierge will no longer be able to enter with it.')) return;
    setBusy('revoke');
    setError(null);
    try {
      await revokeAccess(state.access.id);
      hidePin();
      invalidateSmartLockSummary(orderId);
      setTimeout(load, 2500);
    } catch (err) {
      setError(parseApiError(err, 'Could not revoke access'));
    } finally {
      setBusy(null);
    }
  };

  const copyPin = async () => {
    if (!pin) return;
    try {
      await navigator.clipboard.writeText(pin);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      /* clipboard unavailable */
    }
  };

  const access = state?.access;
  const hasLiveAccess = access && !['revoking', 'revocation_unconfirmed'].includes(access.status);
  const canReveal = access?.hasPin && ['provisioned', 'ready', 'active'].includes(access.status);
  const windowInvalid = useMemo(() => {
    if (!startInput || !endInput || !tz) return true;
    const s = moment.tz(startInput, INPUT_FMT, tz);
    const e = moment.tz(endInput, INPUT_FMT, tz);
    return !s.isValid() || !e.isValid() || !e.isAfter(s) || e.isBefore(moment());
  }, [startInput, endInput, tz]);

  const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-inner focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100';
  const labelCls = 'text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400';

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-sm" onMouseDown={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Smart lock access"
        className="w-full max-w-md rounded-3xl border border-white/30 bg-white/95 shadow-[0_20px_60px_rgba(15,23,42,0.25)]"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Smart lock access</p>
            <p className="mt-0.5 truncate text-sm text-slate-700">
              {orderLabel}
              {state?.mapping?.doorName ? <span className="text-slate-400"> · {state.mapping.doorName}</span> : null}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          {loading && !state ? (
            <div className="flex items-center gap-3 py-6 text-sm text-slate-500">
              <ArrowPathIcon className="h-5 w-5 animate-spin" /> Checking lock status…
            </div>
          ) : null}

          {error ? <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p> : null}

          {state && !state.available && !hasLiveAccess ? (
            <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">{availabilityMessage(state.reason)}</p>
          ) : null}

          {state && access ? (
            <div className="rounded-2xl bg-slate-50 px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <StatusPill status={access.status} />
                <span className="text-[11px] text-slate-400">gen {access.generation}</span>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-sm text-slate-700">
                <div>
                  <dt className={labelCls}>Starts</dt>
                  <dd>{fmtWindow(access.authorizedStartAt, tz)}</dd>
                </div>
                <div>
                  <dt className={labelCls}>Ends</dt>
                  <dd>{fmtWindow(access.authorizedEndAt, tz)}</dd>
                </div>
              </dl>
              <p className="mt-2 text-[11px] text-slate-400">
                Times in property time ({tzAbbr(tz)}){access.windowOverride ? ' · set by admin' : ''}
              </p>
              {access.lastProviderError ? <p className="mt-2 text-xs text-rose-600">Last error: {access.lastProviderError}</p> : null}

              {canReveal ? (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {pin ? (
                    <>
                      <span className="font-mono text-2xl font-bold tracking-[0.3em] text-slate-900">{pin}</span>
                      <button type="button" onClick={copyPin} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-white">
                        {copied ? <CheckIcon className="h-4 w-4 text-emerald-600" /> : <ClipboardDocumentListIcon className="h-4 w-4" />}
                        {copied ? 'Copied' : 'Copy'}
                      </button>
                      <button type="button" onClick={hidePin} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-white">
                        <EyeSlashIcon className="h-4 w-4" /> Hide
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      disabled={busy === 'reveal'}
                      onClick={handleReveal}
                      className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50"
                    >
                      <EyeIcon className="h-4 w-4" /> {busy === 'reveal' ? 'Revealing…' : 'Reveal PIN'}
                    </button>
                  )}
                </div>
              ) : null}
              {pin ? <p className="mt-2 text-[11px] text-slate-400">PIN hides automatically in 60s. Every reveal is logged.</p> : null}
            </div>
          ) : null}

          {state && (state.available || hasLiveAccess) ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className={labelCls}>Start ({tzAbbr(tz)})</span>
                  <input
                    type="datetime-local"
                    className={classNames(inputCls, 'mt-1')}
                    value={startInput}
                    onChange={(e) => {
                      touchedRef.current = true;
                      setStartInput(e.target.value);
                    }}
                  />
                </label>
                <label className="block">
                  <span className={labelCls}>End ({tzAbbr(tz)})</span>
                  <input
                    type="datetime-local"
                    className={classNames(inputCls, 'mt-1')}
                    value={endInput}
                    onChange={(e) => {
                      touchedRef.current = true;
                      setEndInput(e.target.value);
                    }}
                  />
                </label>
              </div>
              <p className="text-[11px] text-slate-400">Defaults to now until 4:00 PM at the property. Max 7 days.</p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={busy === 'provision' || windowInvalid || !state.available}
                  onClick={handleProvision}
                  className="inline-flex items-center gap-1.5 rounded-full bg-sky-500 px-4 py-2 text-xs font-semibold text-white hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy === 'provision' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <LockClosedIcon className="h-4 w-4" />}
                  {busy === 'provision' ? 'Working…' : hasLiveAccess ? 'Update window' : 'Create code'}
                </button>
                {hasLiveAccess ? (
                  <button
                    type="button"
                    disabled={busy === 'revoke'}
                    onClick={handleRevoke}
                    className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 px-4 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                  >
                    <NoSymbolIcon className="h-4 w-4" /> {busy === 'revoke' ? 'Revoking…' : 'Revoke'}
                  </button>
                ) : null}
                <button type="button" onClick={load} className="ml-auto inline-flex items-center gap-1 rounded-full px-3 py-2 text-xs font-medium text-slate-500 hover:bg-slate-100" aria-label="Refresh">
                  <ArrowPathIcon className={classNames('h-4 w-4', loading && 'animate-spin')} /> Refresh
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Compact "Smart Lock" pill for the dispatch order tile. Renders only when the
 * order's property has a RemoteLock door mapped (one batched lookup per list).
 */
export default function SmartLockAccessButton({ orderId, orderLabel, className }) {
  const summary = useSmartLockSummary(orderId);
  const [open, setOpen] = useState(false);
  if (!summary?.mapped) return null;

  const status = summary.status;
  const tone = ['ready', 'active'].includes(status)
    ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300'
    : status
      ? 'bg-sky-50 text-sky-700 hover:bg-sky-100 dark:bg-sky-900/20 dark:text-sky-300'
      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-gray-700 dark:text-gray-200';

  return (
    <div onClick={(e) => e.stopPropagation()} className={className}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={summary.doorName ? `Smart lock: ${summary.doorName}` : 'Smart lock access'}
        className={classNames('flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors', tone)}
      >
        <LockClosedIcon className="h-3.5 w-3.5" />
        {status ? STATUS_LABEL[status] || status : 'Create code'}
      </button>
      {open ? <SmartLockAccessModal orderId={orderId} orderLabel={orderLabel} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}
